import { Fragment, useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
const PAGINATION_THRESHOLD = 50;
export interface SortableColumn<T> {
  key: string;
  label: React.ReactNode;
  width?: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
}
interface SortState {
  key: string | null;
  dir: 'asc' | 'desc';
}
interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  data: T[];
  defaultSort?: {
    key: string;
    dir: 'asc' | 'desc';
  };
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  rowStyle?: (row: T) => React.CSSProperties | undefined;
  className?: string;
  renderExpandedRow?: (row: T) => React.ReactNode;
  footer?: React.ReactNode;
  onSortedDataChange?: (data: T[]) => void;
  pageSize?: number;
}
function getComparableValue<T>(row: T, col: SortableColumn<T>): string | number {
  if (col.sortValue) return col.sortValue(row);
  const val = (row as Record<string, unknown>)[col.key];
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val;
  return String(val ?? '');
}
function compareValues(a: string | number, b: string | number, dir: 'asc' | 'desc'): number {
  let result: number;
  if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = (a as number) - (b as number);
  }
  return dir === 'asc' ? result : -result;
}
export function SortableTable<T>({
  columns,
  data,
  defaultSort,
  onRowClick,
  rowKey,
  rowStyle,
  className = 'tbl',
  renderExpandedRow,
  footer,
  onSortedDataChange,
  pageSize
}: SortableTableProps<T>) {
  const [sort, setSort] = useState<SortState>({
    key: defaultSort?.key ?? null,
    dir: defaultSort?.dir ?? 'desc'
  });
  const [page, setPage] = useState(0);
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    return [...data].sort((a, b) => compareValues(getComparableValue(a, col), getComparableValue(b, col), sort.dir));
  }, [data, sort.key, sort.dir, columns]);
  useEffect(() => {
    onSortedDataChange?.(sortedData);
  }, [sortedData, onSortedDataChange]);
  useEffect(() => {
    setPage(0);
  }, [data, sort.key, sort.dir]);
  const paginated = Boolean(pageSize) && sortedData.length > PAGINATION_THRESHOLD;
  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount - 1);
  const pageData = paginated && pageSize ? sortedData.slice(currentPage * pageSize, currentPage * pageSize + pageSize) : sortedData;
  const handleClick = (colKey: string) => {
    setSort(prev => {
      if (prev.key === colKey) {
        if (prev.dir === 'asc') return {
          key: colKey,
          dir: 'desc'
        };
        return {
          key: null,
          dir: 'desc'
        };
      }
      return {
        key: colKey,
        dir: 'asc'
      };
    });
  };
  const sortIndicator = (colKey: string) => {
    const isActive = sort.key === colKey;
    return <span style={{
      marginLeft: 3,
      fontSize: 8,
      color: isActive ? '#fa0f00' : '#c0c5ce'
    }} data-id="element-1284" data-mp="1g8xocrdg8dy3">
        {isActive ? sort.dir === 'asc' ? '▲' : '▼' : '▲'}
      </span>;
  };
  const renderPagination = (position: 'top' | 'bottom') => {
    if (!paginated || !pageSize) return null;
    const from = currentPage * pageSize + 1;
    const to = Math.min(currentPage * pageSize + pageSize, sortedData.length);
    return <div className={`pgn-bar pgn-${position}`} data-id="element-1285" data-mp="xco39k44qn4l">
        <span className="pgn-count" data-id="element-1286" data-mp="1bamgunammfj1">
          <strong data-id="element-1287" data-mp="1bbnp6k09cnx2">{from}–{to}</strong> of <strong data-id="element-1288" data-mp="1b8rur5nmchdd">{sortedData.length}</strong>
        </span>
        <div className="pgn-controls" data-id="element-1289" data-mp="1787ys5es7jst">
          <button onClick={() => setPage(0)} disabled={currentPage === 0} className="pgn-btn" aria-label="First page" data-id="element-1290" data-mp="17bncp4wpymsn">
            <ChevronsLeft size={13} data-id="element-1291" data-mp="2io6a6fxfmhx7" />
          </button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="pgn-btn" aria-label="Previous page" data-id="element-1292" data-mp="3dz4dd1v4gtbn">
            <ChevronLeft size={13} data-id="element-1293" data-mp="3mz191nfxisg7" />
          </button>
          <span className="pgn-page" data-id="element-1294" data-mp="3n1dlxm6tycme">{currentPage + 1} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={currentPage >= pageCount - 1} className="pgn-btn" aria-label="Next page" data-id="element-1295" data-mp="3rzyovtdgvaej">
            <ChevronRight size={13} data-id="element-1296" data-mp="4rlc8xuekrpj" />
          </button>
          <button onClick={() => setPage(pageCount - 1)} disabled={currentPage >= pageCount - 1} className="pgn-btn" aria-label="Last page" data-id="element-1297" data-mp="9lfrnp2z6kit">
            <ChevronsRight size={13} data-id="element-1298" data-mp="jjn3xumbkeeh" />
          </button>
        </div>
      </div>;
  };
  return <>
      {renderPagination('top')}
      <table className={className} data-id="element-1299" data-mp="ofasq8ww35jg">
        <thead data-id="element-1300" data-mp="28zzhh4aoxq6y">
          <tr data-id="element-1301" data-mp="290r4vru4whm5">
            {columns.map(col => <th key={col.key} style={{
            width: col.width,
            cursor: 'pointer',
            userSelect: 'none',
            textAlign: col.align
          }} onClick={() => handleClick(col.key)} data-id="element-1302" data-mp="240mjpoia6ypl">
                {col.label}{sortIndicator(col.key)}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-1303" data-mp="2mqktc91q8zvc">
          {pageData.map((row, i) => <Fragment key={rowKey(row) || i} data-id="element-1304" data-mp="2mt774qf7obm0" data-mpi={i}>
              <tr onClick={onRowClick ? () => onRowClick(row) : undefined} style={rowStyle?.(row)} data-id="element-1305" data-mp="2hzm6eqm8h3j4">
                {columns.map(col => <td key={col.key} style={{
              textAlign: col.align
            }} data-id="element-1306" data-mp="2d118wyyd8xyd">
                    {col.render(row)}
                  </td>)}
              </tr>
              {renderExpandedRow?.(row)}
            </Fragment>)}
        </tbody>
        {footer && <tfoot data-id="element-1307" data-mp="13thhj1nbts91">{footer}</tfoot>}
      </table>
      {renderPagination('bottom')}
    </>;
}